import React, { useEffect, useState } from 'react';
import api from '../api';
import useAdminDialog from '../components/AdminDialog';
import { useAdminLanguage } from '../AdminLanguageContext';

const yuan = (cents) => (Number(cents || 0) / 100).toFixed(2);

export default function Withdrawals() {
  const { copy } = useAdminLanguage();
  const withdrawalsCopy = copy.withdrawals;
  const statusOptions = [
    { value: 'pending', icon: '⏳', label: withdrawalsCopy.status.pending },
    { value: 'approved', icon: '✅', label: withdrawalsCopy.status.approved },
    { value: 'rejected', icon: '❌', label: withdrawalsCopy.status.rejected },
    { value: 'all', icon: '', label: withdrawalsCopy.status.all },
  ];
  const statusLabel = {
    pending: withdrawalsCopy.status.pending,
    approved: withdrawalsCopy.status.paid,
    rejected: withdrawalsCopy.status.rejected,
  };
  const [status, setStatus] = useState('pending');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const { confirmAction, requestText, dialog } = useAdminDialog();

  const load = () => {
    setLoading(true);
    api.get('/admin/finance/withdrawals', { params: { status } })
      .then(r => setList(r.data.withdrawals || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (w) => {
    const note = await requestText({
      title: withdrawalsCopy.approve.promptTitle,
      message: withdrawalsCopy.approve.promptMessage(yuan(w.amount), w.username || withdrawalsCopy.emptyValue),
      placeholder: withdrawalsCopy.approve.promptPlaceholder,
      defaultValue: '',
    });
    if (note === null) return;
    if (!await confirmAction({ title: withdrawalsCopy.approve.confirmTitle, message: withdrawalsCopy.approve.confirmMessage })) return;
    api.post(`/admin/finance/withdrawals/${w.id}/approve`, { note })
      .then(() => load())
      .catch(e => setErr(e.response?.data?.error || e.message));
  };

  const reject = async (w) => {
    const note = await requestText({
      title: withdrawalsCopy.reject.promptTitle,
      message: withdrawalsCopy.reject.promptMessage,
      required: true,
      placeholder: withdrawalsCopy.reject.promptPlaceholder,
      variant: 'danger',
    });
    if (!note || !note.trim()) return;
    if (!await confirmAction({ title: withdrawalsCopy.reject.confirmTitle, message: withdrawalsCopy.reject.confirmMessage(yuan(w.amount)), variant: 'danger' })) return;
    api.post(`/admin/finance/withdrawals/${w.id}/reject`, { note: note.trim() })
      .then(() => load())
      .catch(e => setErr(e.response?.data?.error || e.message));
  };

  const parseAccount = (json) => {
    try {
      const a = JSON.parse(json || '{}');
      return a;
    } catch { return {}; }
  };

  const total = list.reduce((s, w) => s + Number(w.amount || 0), 0);

  return (
    <div>
      {dialog}
      <div className="page-title">💸 {withdrawalsCopy.title}</div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {statusOptions.map(option => (
            <button key={option.value}
              onClick={() => setStatus(option.value)}
              className={status === option.value ? 'btn btn-primary' : 'btn btn-outline'}
              style={{ padding: '6px 14px', fontSize: 13 }}>
              {option.icon ? `${option.icon} ${option.label}` : option.label}
            </button>
          ))}
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
          {withdrawalsCopy.summary(list.length, yuan(total))}
        </span>
      </div>

      {err && <div style={{ background: '#fef2f2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>{err}</div>}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{withdrawalsCopy.columns.id}</th><th>{withdrawalsCopy.columns.user}</th><th>{withdrawalsCopy.columns.amount}</th>
                <th>{withdrawalsCopy.columns.account}</th><th>{withdrawalsCopy.columns.status}</th><th>{withdrawalsCopy.columns.createdAt}</th><th>{withdrawalsCopy.columns.note}</th><th>{withdrawalsCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{withdrawalsCopy.loading}</td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{withdrawalsCopy.empty}</td></tr>
              ) : list.map(w => {
                const a = parseAccount(w.account_info);
                const accountType = a.type === 'alipay'
                  ? withdrawalsCopy.accountTypes.alipay
                  : a.type === 'bank'
                    ? withdrawalsCopy.accountTypes.bank
                    : a.type || withdrawalsCopy.emptyValue;
                const accountOwner = [a.name, a.bank].filter(Boolean).join(' · ');
                return (
                  <tr key={w.id}>
                    <td style={{ color: '#9ca3af' }}>#{w.id}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{w.username}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{w.email}</div>
                    </td>
                    <td style={{ color: '#1dbf73', fontWeight: 700, fontSize: 15 }}>¥ {yuan(w.amount)}</td>
                    <td style={{ fontSize: 12 }}>
                      <div><strong>{accountType}</strong></div>
                      <div>{a.account || withdrawalsCopy.emptyValue}</div>
                      <div style={{ color: '#9ca3af' }}>{accountOwner}</div>
                    </td>
                    <td>
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        background: w.status === 'pending' ? '#fef3c7' : w.status === 'approved' ? '#dcfce7' : '#fee2e2',
                        color: w.status === 'pending' ? '#92400e' : w.status === 'approved' ? '#166534' : '#991b1b',
                      }}>
                        {statusLabel[w.status] || w.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                      {w.created_at ? String(w.created_at).slice(0, 16) : withdrawalsCopy.emptyValue}
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 140 }}>{w.admin_note || withdrawalsCopy.emptyValue}</td>
                    <td>
                      {w.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-sm btn-primary" onClick={() => approve(w)}>✅ {withdrawalsCopy.actions.approve}</button>
                          <button className="btn btn-sm btn-outline" style={{ color: '#ef4444', borderColor: '#fecaca' }} onClick={() => reject(w)}>❌ {withdrawalsCopy.actions.reject}</button>
                        </div>
                      ) : <span style={{ color: '#9ca3af', fontSize: 12 }}>{withdrawalsCopy.status.handled}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
